<template>
  <div class="container-fluid certificate-generator">
    <!-- Header -->
    <div class="d-sm-flex align-items-center justify-content-between mb-4">
      <h1 class="h3 mb-0 text-gray-800">
        <i class="bi bi-award"></i> Gerador de Certificados
      </h1>
      <div>
        <button v-if="!isEditing" @click="createNewGenerator" class="btn btn-primary">
          <i class="bi bi-plus-circle"></i> Novo Gerador
        </button>
        <button v-if="isEditing" @click="saveGenerator" class="btn btn-success me-2">
          <i class="bi bi-check-circle"></i> Salvar
        </button>
        <button v-if="isEditing" @click="cancelEdit" class="btn btn-secondary">
          <i class="bi bi-x-circle"></i> Cancelar
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="d-flex justify-content-center">
      <Loader />
    </div>

    <!-- Main Content -->
    <div v-else class="row">
      <!-- Form Section -->
      <div class="col-lg-3 col-xl-3">
        <div class="card shadow mb-4">
          <div class="card-header py-3">
            <h6 class="m-0 font-weight-bold text-primary">
              <i class="bi bi-gear"></i> Configurações do Gerador
            </h6>
          </div>
          <div class="card-body">
            <form @submit.prevent="saveGenerator">
              <!-- Basic Settings -->
              <div class="mb-3">
                <h6 class="font-weight-bold text-dark mb-2">Configurações Básicas</h6>

                <div class="mb-2">
                  <label class="form-label small mb-1">Nome do Gerador *</label>
                  <input v-model="generator.name" type="text" class="form-control form-control-sm" required
                    placeholder="Ex: Certificado Básico" />
                </div>

                <div class="mb-2">
                  <label class="form-label small mb-1">URL do Template *</label>
                  <input v-model="generator.template_url" type="url" class="form-control form-control-sm" required
                    placeholder="https://exemplo.com/template.png" />
                  <div class="form-text">Formatos aceitos: .jpg, .jpeg, .png, .gif, .bmp, .webp</div>
                </div>

                <div class="mb-2">
                  <label class="form-label small mb-1">Ajuste da Imagem</label>
                  <select v-model="generator.template_fit" class="form-select form-select-sm">
                    <option value="contain">Mostrar Completa (sem cortes)</option>
                    <option value="cover">Preencher Área (pode cortar)</option>
                    <option value="fill">Esticar para Preencher</option>
                    <option value="scale-down">Reduzir se Necessário</option>
                  </select>
                  <small class="text-muted">Como a imagem deve se ajustar ao espaço disponível</small>
                </div>

                <div class="mb-2"
                  v-if="generator.template_fit === 'contain' || generator.template_fit === 'scale-down'">
                  <label class="form-label small mb-1">Cor de Fundo</label>
                  <div class="d-flex gap-2">
                    <input v-model="generator.background_color" type="color"
                      class="form-control form-control-sm form-control-color" style="max-width: 60px;" />
                    <input v-model="generator.background_color" type="text" class="form-control form-control-sm"
                      placeholder="#ffffff" />
                  </div>
                  <small class="text-muted">Cor de fundo quando a imagem não preenche toda a área</small>
                </div>

                <div class="mb-2">
                  <label class="form-label small mb-1">URL da Fonte</label>
                  <input v-model="generator.font_url" type="url" class="form-control form-control-sm"
                    placeholder="https://exemplo.com/font.ttf" />
                  <div class="form-text">Formatos aceitos: .ttf, .otf, .woff, .woff2</div>
                </div>
              </div>

              <!-- Student Name Settings -->
              <div class="mb-3">
                <h6 class="font-weight-bold text-dark mb-2">Nome do Estudante</h6>

                <div class="row g-2">
                  <div class="col-md-6">
                    <label class="form-label small mb-1">Coordenadas (X,Y)</label>
                    <input v-model="generator.student_name_coordinates" type="text" class="form-control form-control-sm"
                      placeholder="200,300" @input="validateCoordinateInput('student_name_coordinates')" />
                    <small class="text-muted">Formato: X,Y (área dinâmica)</small>
                  </div>
                  <div class="col-md-3">
                    <label class="form-label small mb-1">Tamanho</label>
                    <input v-model.number="generator.student_name_font_size" type="number"
                      class="form-control form-control-sm" min="8" max="200" />
                  </div>
                  <div class="col-md-3">
                    <label class="form-label small mb-1">Cor</label>
                    <input v-model="generator.student_name_color" type="color"
                      class="form-control form-control-color form-control-sm" />
                  </div>
                </div>
              </div>

              <!-- Header Text Settings -->
              <div class="mb-3">
                <h6 class="font-weight-bold text-dark mb-2">Texto do Cabeçalho</h6>

                <div class="mb-2">
                  <label class="form-label small mb-1">Texto *</label>
                  <input v-model="generator.header_text" type="text" class="form-control form-control-sm" required
                    placeholder="CERTIFICADO DE CONCLUSÃO" />
                </div>

                <div class="row g-2">
                  <div class="col-md-6">
                    <label class="form-label small mb-1">Coordenadas (X,Y)</label>
                    <input v-model="generator.header_text_coordinates" type="text" class="form-control form-control-sm"
                      placeholder="400,100" />
                  </div>
                  <div class="col-md-3">
                    <label class="form-label small mb-1">Tamanho</label>
                    <input v-model.number="generator.header_text_font_size" type="number"
                      class="form-control form-control-sm" min="8" max="200" />
                  </div>
                  <div class="col-md-3">
                    <label class="form-label small mb-1">Cor</label>
                    <input v-model="generator.header_text_color" type="color"
                      class="form-control form-control-color form-control-sm" />
                  </div>
                </div>
              </div>

              <!-- Token Settings -->
              <div class="mb-3">
                <h6 class="font-weight-bold text-dark mb-2">Token</h6>

                <div class="row g-2">
                  <div class="col-md-6">
                    <label class="form-label small mb-1">Coordenadas (X,Y)</label>
                    <input v-model="generator.token_coordinates" type="text" class="form-control form-control-sm"
                      placeholder="50,500" />
                  </div>
                  <div class="col-md-3">
                    <label class="form-label small mb-1">Tamanho</label>
                    <input v-model.number="generator.token_font_size" type="number" class="form-control form-control-sm"
                      min="8" max="200" />
                  </div>
                  <div class="col-md-3">
                    <label class="form-label small mb-1">Cor</label>
                    <input v-model="generator.token_color" type="color"
                      class="form-control form-control-color form-control-sm" />
                  </div>
                </div>
              </div>

              <!-- Start Date Settings -->
              <div class="mb-3">
                <h6 class="font-weight-bold text-dark mb-2">Data de Início</h6>

                <div class="form-check mb-2">
                  <input v-model="generator.show_start_date" type="checkbox" class="form-check-input"
                    id="showStartDate" />
                  <label class="form-check-label small" for="showStartDate">
                    Mostrar data de início
                  </label>
                </div>

                <div v-if="generator.show_start_date" class="row g-2">
                  <div class="col-md-6">
                    <label class="form-label small mb-1">Prefix</label>
                    <input v-model="generator.start_date_prefix" type="text" class="form-control form-control-sm"
                      placeholder="Início:" />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label small mb-1">Coordenadas (X,Y)</label>
                    <input v-model="generator.start_date_coordinates" type="text" class="form-control form-control-sm"
                      placeholder="100,400" />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label small mb-1">Tamanho</label>
                    <input v-model.number="generator.start_date_font_size" type="number"
                      class="form-control form-control-sm" min="8" max="200" />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label small mb-1">Cor</label>
                    <input v-model="generator.start_date_color" type="color"
                      class="form-control form-control-color form-control-sm" />
                  </div>
                </div>
              </div>

              <!-- End Date Settings -->
              <div class="mb-3">
                <h6 class="font-weight-bold text-dark mb-2">Data de Fim</h6>

                <div class="form-check mb-2">
                  <input v-model="generator.show_end_date" type="checkbox" class="form-check-input" id="showEndDate" />
                  <label class="form-check-label small" for="showEndDate">
                    Mostrar data de fim
                  </label>
                </div>

                <div v-if="generator.show_end_date" class="row g-2">
                  <div class="col-md-6">
                    <label class="form-label small mb-1">Prefix</label>
                    <input v-model="generator.end_date_prefix" type="text" class="form-control form-control-sm"
                      placeholder="Conclusão:" />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label small mb-1">Coordenadas (X,Y)</label>
                    <input v-model="generator.end_date_coordinates" type="text" class="form-control form-control-sm"
                      placeholder="100,450" />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label small mb-1">Tamanho</label>
                    <input v-model.number="generator.end_date_font_size" type="number"
                      class="form-control form-control-sm" min="8" max="200" />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label small mb-1">Cor</label>
                    <input v-model="generator.end_date_color" type="color"
                      class="form-control form-control-color form-control-sm" />
                  </div>
                </div>
              </div>

              <!-- Central Text Settings -->
              <div class="mb-3">
                <h6 class="font-weight-bold text-dark mb-2">Texto Central</h6>

                <div class="mb-2">
                  <label class="form-label small mb-1">Texto *</label>
                  <textarea v-model="generator.central_text" class="form-control form-control-sm" rows="3" required
                    placeholder="Certificamos que [NOME] concluiu com êxito o curso..."></textarea>
                </div>

                <div class="row g-2">
                  <div class="col-md-6">
                    <label class="form-label small mb-1">Coordenadas (X,Y)</label>
                    <input v-model="generator.central_text_coordinates" type="text" class="form-control form-control-sm"
                      placeholder="200,250" />
                  </div>
                  <div class="col-md-3">
                    <label class="form-label small mb-1">Tamanho</label>
                    <input v-model.number="generator.central_text_font_size" type="number"
                      class="form-control form-control-sm" min="8" max="200" />
                  </div>
                  <div class="col-md-3">
                    <label class="form-label small mb-1">Cor</label>
                    <input v-model="generator.central_text_color" type="color"
                      class="form-control form-control-color form-control-sm" />
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      <!-- Preview Section -->
      <div class="col-lg-9 col-xl-9">
        <div class="card shadow mb-4">
          <div class="card-header py-3 d-flex justify-content-between align-items-center">
            <h6 class="m-0 font-weight-bold text-primary">
              <i class="bi bi-eye"></i> Preview do Certificado
            </h6>
            <div>
              <button @click="refreshPreview" class="btn btn-sm btn-outline-primary me-2">
                <i class="bi bi-arrow-clockwise"></i> Atualizar
              </button>
              <button @click="toggleFullscreen" class="btn btn-sm btn-outline-secondary">
                <i class="bi bi-fullscreen"></i> Tela Cheia
              </button>
            </div>
          </div>
          <div class="card-body">
            <!-- Preview Controls -->
            <div class="row g-2 mb-3">
              <div class="col-md-3">
                <label class="form-label small mb-1">Nome (Preview)</label>
                <input v-model="previewData.studentName" type="text" class="form-control form-control-sm"
                  placeholder="João Silva" />
              </div>
              <div class="col-md-3">
                <label class="form-label small mb-1">Token (Preview)</label>
                <input v-model="previewData.token" type="text" class="form-control form-control-sm"
                  placeholder="CERT-2024-001" />
              </div>
              <div class="col-md-3">
                <label class="form-label small mb-1">Data Início</label>
                <input v-model="previewData.startDate" type="date" class="form-control form-control-sm" />
              </div>
              <div class="col-md-3">
                <label class="form-label small mb-1">Data Fim</label>
                <input v-model="previewData.endDate" type="date" class="form-control form-control-sm" />
              </div>
            </div>



            <!-- Certificate Preview -->
            <div class="certificate-preview">
              <div class="preview-container" :style="{ backgroundColor: generator.background_color || '#ffffff' }">

                <!-- Template Background (sempre fixo no fundo, opcional) -->
                <img v-if="generator.template_url" :src="generator.template_url"
                  :class="['template-image', `template-fit-${generator.template_fit || 'contain'}`]" alt="Template" />

                <!-- Overlay Text Elements -->
                <!-- Nome do Estudante -->
                <div class="text-overlay student-name draggable-element" :style="getTextStyle('student_name')"
                  draggable="true" @dragstart="handleDragStart($event, 'student_name')"
                  @dragend="handleDragEnd($event, 'student_name')" @mousedown="handleMouseDown($event, 'student_name')"
                  @mousemove="handleMouseMove($event, 'student_name')" @mouseup="handleMouseUp($event, 'student_name')">
                  <div class="element-handle">
                    <i class="bi bi-grip-vertical"></i>
                  </div>
                  {{ previewData.studentName || '[NOME DO ESTUDANTE]' }}
                </div>

                <!-- Header Text -->
                <div v-if="generator.header_text" class="text-overlay header-text draggable-element"
                  :style="getTextStyle('header_text')" draggable="true"
                  @dragstart="handleDragStart($event, 'header_text')" @dragend="handleDragEnd($event, 'header_text')"
                  @mousedown="handleMouseDown($event, 'header_text')"
                  @mousemove="handleMouseMove($event, 'header_text')" @mouseup="handleMouseUp($event, 'header_text')">
                  <div class="element-handle">
                    <i class="bi bi-grip-vertical"></i>
                  </div>
                  {{ generator.header_text }}
                </div>

                <!-- Token -->
                <div class="text-overlay token draggable-element" :style="getTextStyle('token')" draggable="true"
                  @dragstart="handleDragStart($event, 'token')" @dragend="handleDragEnd($event, 'token')"
                  @mousedown="handleMouseDown($event, 'token')" @mousemove="handleMouseMove($event, 'token')"
                  @mouseup="handleMouseUp($event, 'token')">
                  <div class="element-handle">
                    <i class="bi bi-grip-vertical"></i>
                  </div>
                  {{ previewData.token || '[TOKEN]' }}
                </div>

                <!-- Data de Início -->
                <div v-if="generator.show_start_date" class="text-overlay start-date draggable-element"
                  :style="getTextStyle('start_date')" draggable="true"
                  @dragstart="handleDragStart($event, 'start_date')" @dragend="handleDragEnd($event, 'start_date')"
                  @mousedown="handleMouseDown($event, 'start_date')" @mousemove="handleMouseMove($event, 'start_date')"
                  @mouseup="handleMouseUp($event, 'start_date')">
                  <div class="element-handle">
                    <i class="bi bi-grip-vertical"></i>
                  </div>
                  {{ generator.start_date_prefix }} {{ formatDate(previewData.startDate) }}
                </div>

                <!-- Data de Fim -->
                <div v-if="generator.show_end_date" class="text-overlay end-date draggable-element"
                  :style="getTextStyle('end_date')" draggable="true" @dragstart="handleDragStart($event, 'end_date')"
                  @dragend="handleDragEnd($event, 'end_date')" @mousedown="handleMouseDown($event, 'end_date')"
                  @mousemove="handleMouseMove($event, 'end_date')" @mouseup="handleMouseUp($event, 'end_date')">
                  <div class="element-handle">
                    <i class="bi bi-grip-vertical"></i>
                  </div>
                  {{ generator.end_date_prefix }} {{ formatDate(previewData.endDate) }}
                </div>

                <!-- Texto Central -->
                <div v-if="generator.central_text" class="text-overlay central-text draggable-element"
                  :style="getTextStyle('central_text')" draggable="true"
                  @dragstart="handleDragStart($event, 'central_text')" @dragend="handleDragEnd($event, 'central_text')"
                  @mousedown="handleMouseDown($event, 'central_text')"
                  @mousemove="handleMouseMove($event, 'central_text')" @mouseup="handleMouseUp($event, 'central_text')">
                  <div class="element-handle">
                    <i class="bi bi-grip-vertical"></i>
                  </div>
                  {{ generator.central_text.replace('[NOME]', previewData.studentName || '[NOME]') }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Generators List -->
    <div class="card shadow mb-4">
      <div class="card-header py-3">
        <h6 class="m-0 font-weight-bold text-primary">
          <i class="bi bi-list"></i> Geradores Cadastrados
        </h6>
      </div>
      <div class="card-body">
        <div class="table-responsive">
          <table class="table table-bordered">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Template</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="gen in generators" :key="gen.id">
                <td>{{ gen.name }}</td>
                <td>
                  <a :href="gen.template_url" target="_blank" class="btn btn-sm btn-outline-info">
                    <i class="bi bi-eye"></i> Ver Template
                  </a>
                </td>
                <td>
                  <span class="badge bg-success">Ativo</span>
                </td>
                <td>
                  <button @click="editGenerator(gen)" class="btn btn-sm btn-primary me-1">
                    <i class="bi bi-pencil"></i> Editar
                  </button>
                  <button @click="deleteGenerator(gen.id)" class="btn btn-sm btn-danger">
                    <i class="bi bi-trash"></i> Excluir
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { http } from "../../http";
  import Loader from "../shared/painel/Loader.vue";

  export default {
    name: "CertificateGenerator",
    components: { Loader },

    data() {
      return {
        loading: false,
        isEditing: false,
        generators: [],
        generator: this.getDefaultGenerator(),
        previewData: {
          studentName: "João Silva",
          token: "CERT-2024-001",
          startDate: "2024-01-15",
          endDate: "2024-03-15"
        },
        draggedElement: null,
        dragStartPos: { x: 0, y: 0 },
        dragOffset: { x: 0, y: 0 }
      };
    },

    mounted() {
      this.loadGenerators();

      // Adicionar listener para mudanças de tela cheia
      document.addEventListener('fullscreenchange', this.handleFullscreenChange);
      document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange);
      document.addEventListener('mozfullscreenchange', this.handleFullscreenChange);
      document.addEventListener('MSFullscreenChange', this.handleFullscreenChange);
    },

    beforeUnmount() {
      // Remover listeners
      document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', this.handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', this.handleFullscreenChange);
    },

    methods: {
      getDefaultGenerator() {
        return {
          name: "Novo Gerador de Certificado",
          template_url: "https://via.placeholder.com/800x600/f0f0f0/333.png",
          template_fit: "contain",
          background_color: "#ffffff",
          font_url: "https://fonts.gstatic.com/s/roboto/v30/KFOmCnqEu92Fr1Mu4mxK.woff2",
          student_name_coordinates: "300,380", // Centralizado horizontalmente
          student_name_font_size: 24, // Tamanho menor para corresponder ao PDF
          student_name_color: "#2ea3f2",
          header_text: "CERTIFICADO DE CONCLUSÃO",
          header_text_coordinates: "200,100", // Posição do cabeçalho
          header_text_font_size: 20, // Tamanho menor
          header_text_color: "#000000",
          token_coordinates: "650,380", // Lado direito, alinhado com nome
          token_font_size: 12, // Tamanho menor
          token_color: "#000000",
          show_start_date: false,
          start_date_prefix: "Início:",
          start_date_coordinates: "200,450", // Esquerda-baixo
          start_date_font_size: 14, // Tamanho menor
          start_date_color: "#000000",
          show_end_date: true,
          end_date_prefix: "Conclusão:",
          end_date_coordinates: "600,450", // Direita-baixo
          end_date_font_size: 14, // Tamanho menor
          end_date_color: "#000000",
          central_text: "Certificamos que [NOME] concluiu com êxito o curso de desenvolvimento web, demonstrando excelente desempenho e dedicação durante todo o período de estudos.",
          central_text_coordinates: "150,250", // Posição do texto central
          central_text_font_size: 14, // Tamanho menor
          central_text_color: "#000000"
        };
      },

      async loadGenerators() {
        this.loading = true;
        try {
          // Debug: verificar token
          const token = this.$cookies.get("user_session");
          console.log("Token sendo usado:", token);

          const response = await http.get("generators/");
          console.log("=== RESPOSTA LOAD GENERATORS ===");
          console.log("Resposta completa do loadGenerators:", response.data);
          console.log("Tipo da resposta:", typeof response.data);
          if (Array.isArray(response.data)) {
            console.log("Array de geradores:", response.data.length, "itens");
            response.data.forEach((gen, index) => {
              console.log(`Gerador ${index}:`, gen);
              console.log(`ID do gerador ${index}:`, gen.id, typeof gen.id);
              console.log(`Propriedades do gerador ${index}:`, Object.keys(gen));
            });
          }
          this.generators = response.data;
        } catch (error) {
          console.error("Erro ao carregar geradores:", error);
          console.error("Detalhes do erro:", error.response?.data);
          this.showAlert("Erro ao carregar geradores: " + (error.response?.data?.detail || error.message), "error");
        } finally {
          this.loading = false;
        }
      },

      createNewGenerator() {
        this.generator = this.getDefaultGenerator();
        this.isEditing = true;
      },

      editGenerator(gen) {
        this.generator = { ...gen };
        this.isEditing = true;
      },

      async saveGenerator() {
        try {
          // Validação frontend antes de enviar
          if (!this.generator.name || this.generator.name.trim() === "") {
            this.showAlert("Nome do gerador é obrigatório", "error");
            return;
          }

          // Validar formato das URLs se fornecidas
          if (this.generator.template_url && this.generator.template_url.trim() !== "") {
            const imageExtensions = /\.(jpg|jpeg|png|gif|bmp|webp)(\?.*)?$/i;
            if (!imageExtensions.test(this.generator.template_url)) {
              this.showAlert("URL do template deve apontar para uma imagem válida (.jpg, .jpeg, .png, .gif, .bmp, .webp)", "error");
              return;
            }
          }

          if (this.generator.font_url && this.generator.font_url.trim() !== "") {
            const fontExtensions = /\.(ttf|otf|woff|woff2)(\?.*)?$/i;
            if (!fontExtensions.test(this.generator.font_url)) {
              this.showAlert("URL da fonte deve apontar para um arquivo de fonte válido (.ttf, .otf, .woff, .woff2)", "error");
              return;
            }
          }

          // Garantir que campos obrigatórios tenham valores padrão se estiverem vazios
          const dataToSend = { ...this.generator };

          // Valores padrão para campos obrigatórios
          if (!dataToSend.template_url || dataToSend.template_url.trim() === "") {
            dataToSend.template_url = "https://via.placeholder.com/800x600/f0f0f0/333.png";
          }
          if (!dataToSend.font_url || dataToSend.font_url.trim() === "") {
            dataToSend.font_url = "https://fonts.gstatic.com/s/roboto/v30/KFOmCnqEu92Fr1Mu4mxK.woff2";
          }
          if (!dataToSend.student_name_coordinates || dataToSend.student_name_coordinates.trim() === "") {
            dataToSend.student_name_coordinates = "300,380";
          }
          if (!dataToSend.header_text_coordinates || dataToSend.header_text_coordinates.trim() === "") {
            dataToSend.header_text_coordinates = "200,100";
          }
          if (!dataToSend.token_coordinates || dataToSend.token_coordinates.trim() === "") {
            dataToSend.token_coordinates = "650,380";
          }
          if (!dataToSend.central_text_coordinates || dataToSend.central_text_coordinates.trim() === "") {
            dataToSend.central_text_coordinates = "150,250";
          }


          if (dataToSend.id) {
            const response = await http.patch(`generators/${dataToSend.id}/`, dataToSend);
          } else {
            const response = await http.post("generators/", dataToSend);
          }

          this.isEditing = false;
          this.loadGenerators();
          this.showAlert("✅ Gerador salvo com sucesso!", "success");
        } catch (error) {
          if (error.response?.data && typeof error.response.data === 'object') {
            const errorMessages = [];
            for (const [field, messages] of Object.entries(error.response.data)) {
              if (Array.isArray(messages)) {
                errorMessages.push(`${field}: ${messages.join(', ')}`);
              } else {
                errorMessages.push(`${field}: ${messages}`);
              }
            }
            this.showAlert("❌ Erros de validação:<br/>" + errorMessages.join('<br/>'), "error");
          } else {
            this.showAlert("Erro ao salvar gerador: " + (error.response?.data?.detail || error.message), "error");
          }
        }
      },

      async deleteGenerator(id) {
        if (confirm("Tem certeza que deseja excluir este gerador?")) {
          try {
            await http.delete(`generators/${id}/`);
            this.loadGenerators();
            this.showAlert("Gerador excluído com sucesso!", "success");
          } catch (error) {
            this.showAlert("Erro ao excluir gerador", "error");
          }
        }
      },

      cancelEdit() {
        this.isEditing = false;
        this.generator = this.getDefaultGenerator();
      },

      async refreshPreview() {
        try {


          // Se estamos editando um gerador existente, salvar no backend
          if (this.isEditing && this.generator.id) {

            await this.updateGeneratorInBackend();
            // Sucesso já é tratado dentro de updateGeneratorInBackend

          } else {
            // Se não tem ID ou não está editando, apenas atualiza o preview
            this.showAlert("Preview atualizado! Use 'Salvar' para persistir as alterações.", "info");
          }

          // Força re-render do preview
          this.$forceUpdate();
        } catch (error) {

          this.showAlert("❌ Erro ao atualizar preview: " + (error.message || "Erro desconhecido"), "error");
        }
      },

      async updateGeneratorInBackend() {
        try {

          // Verificar se temos ID válido
          if (!this.generator.id) {
            this.showAlert("Erro: Gerador não tem ID válido para atualização", "error");
            return;
          }

          // Preparar dados para envio (mesmo processo do saveGenerator)
          const dataToSend = { ...this.generator };

          // Garantir que campos obrigatórios tenham valores padrão se estiverem vazios
          if (!dataToSend.template_url || dataToSend.template_url.trim() === "") {
            dataToSend.template_url = "https://via.placeholder.com/800x600/f0f0f0/333.png";
          }
          if (!dataToSend.font_url || dataToSend.font_url.trim() === "") {
            dataToSend.font_url = "https://fonts.gstatic.com/s/roboto/v30/KFOmCnqEu92Fr1Mu4mxK.woff2";
          }
          if (!dataToSend.student_name_coordinates || dataToSend.student_name_coordinates.trim() === "") {
            dataToSend.student_name_coordinates = "300,380";
          }
          if (!dataToSend.header_text_coordinates || dataToSend.header_text_coordinates.trim() === "") {
            dataToSend.header_text_coordinates = "200,100";
          }
          if (!dataToSend.token_coordinates || dataToSend.token_coordinates.trim() === "") {
            dataToSend.token_coordinates = "650,380";
          }
          if (!dataToSend.central_text_coordinates || dataToSend.central_text_coordinates.trim() === "") {
            dataToSend.central_text_coordinates = "150,250";
          }



          const response = await http.patch(`generators/${dataToSend.id}/`, dataToSend);


          this.showAlert("✅ Alterações salvas com sucesso no backend!", "success");

          // Atualizar o gerador local com os dados retornados
          this.generator = { ...response.data };
        } catch (error) {


          // Verificar se é erro de autenticação
          if (error.response?.status === 401) {
            this.showAlert("❌ Erro de autenticação. Faça login novamente.", "error");
            // Opcionalmente redirecionar para login
            // window.location.href = '/login';
            return;
          }

          // Mostrar erros de validação específicos
          if (error.response?.data && typeof error.response.data === 'object') {
            const errorMessages = [];
            for (const [field, messages] of Object.entries(error.response.data)) {
              if (Array.isArray(messages)) {
                errorMessages.push(`${field}: ${messages.join(', ')}`);
              } else {
                errorMessages.push(`${field}: ${messages}`);
              }
            }
            this.showAlert("❌ Erros de validação:<br/>" + errorMessages.join('<br/>'), "error");
          } else {
            this.showAlert("❌ Erro ao atualizar: " + (error.response?.data?.detail || error.message), "error");
          }
        }
      },

      getTextStyle(field) {
        const coordinates = this.generator[`${field}_coordinates`];
        const fontSize = this.generator[`${field}_font_size`];
        const color = this.generator[`${field}_color`];

        // Se não há coordenadas, usar posições padrão baseadas no tipo de campo
        let defaultCoords = coordinates;
        if (!coordinates) {
          const defaultPositions = {
            'student_name': '50%,70%',
            'header_text': '50%,15%',
            'token': '5%,90%',
            'start_date': '5%,80%',
            'end_date': '5%,85%',
            'central_text': '50%,45%'
          };
          defaultCoords = defaultPositions[field] || '100,100';
        }

        return this.getValidatedStyle(defaultCoords, fontSize, color);
      },

      formatDate(dateString) {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('pt-BR');
      },

      showAlert(message, type) {
        // Usar o plugin global de alertas
        this.$showAlert(message, type);
      },





      toggleFullscreen() {
        const certificatePreview = document.querySelector('.certificate-preview');
        if (certificatePreview) {
          if (certificatePreview.requestFullscreen) {
            certificatePreview.requestFullscreen();
          } else if (certificatePreview.webkitRequestFullscreen) {
            certificatePreview.webkitRequestFullscreen();
          } else if (certificatePreview.msRequestFullscreen) {
            certificatePreview.msRequestFullscreen();
          }
        }
      },

      validateCoordinates(coordinates) {
        const [x, y] = coordinates.split(',').map(Number);

        // Obter dimensões reais da área de preview (certificate-preview)
        const certificatePreview = document.querySelector('.certificate-preview');
        const maxX = certificatePreview ? certificatePreview.offsetWidth : 1200;
        const maxY = certificatePreview ? certificatePreview.offsetHeight : 800;

        return {
          x: x, // Permitir qualquer coordenada X
          y: y  // Permitir qualquer coordenada Y
        };
      },

      getValidatedStyle(coordinates, fontSize, color) {
        if (!coordinates) return {};

        const [x, y] = coordinates.split(',').map(Number);

        // ANÁLISE DETALHADA BASEADA NO CÓDIGO PIL E IMAGENS COMPARATIVAS
        // PIL usa coordenadas absolutas em pixels do canto superior esquerdo

        // Verificar se temos uma imagem template carregada para obter dimensões reais
        const templateImg = document.querySelector('.certificate-preview img');
        let templateWidth = 800;   // Padrão
        let templateHeight = 600;  // Padrão

        // Se a imagem está carregada, usar suas dimensões naturais
        if (templateImg && templateImg.naturalWidth && templateImg.naturalHeight) {
          templateWidth = templateImg.naturalWidth;
          templateHeight = templateImg.naturalHeight;
        }

        // Calcular porcentagens baseadas nas dimensões reais da template
        const xPercent = (x / templateWidth) * 100;
        const yPercent = (y / templateHeight) * 100;

        // Ajustar font size para proporcionalidade com a tela
        // Usar fator de escala baseado no container atual vs template original
        const certificatePreview = document.querySelector('.certificate-preview');
        const containerWidth = certificatePreview ? certificatePreview.offsetWidth : 800;
        const scaleFactor = containerWidth / templateWidth;
        const adjustedFontSize = fontSize * scaleFactor;

        return {
          left: `${xPercent}%`,
          top: `${yPercent}%`,
          fontSize: `${Math.max(adjustedFontSize, 8)}px`, // Mínimo de 8px para legibilidade
          color: color,
          position: 'absolute',
          fontFamily: 'Arial, sans-serif',
          fontWeight: 'normal', // PIL não usa bold por padrão
          textAlign: 'left',    // PIL posiciona do canto superior esquerdo
          maxWidth: `${Math.min(80, (templateWidth - x) / templateWidth * 100)}%`, // Não ultrapassar borda direita
          wordWrap: 'break-word',
          overflowWrap: 'break-word',
          lineHeight: '1.2',
          whiteSpace: 'pre-wrap',
          transform: 'none', // PIL posiciona exatamente onde especificado
          display: 'block'
        };
      },

      validateCoordinateInput(field) {
        const value = this.generator[field];
        if (!value) return;

        const coords = value.split(',');
        if (coords.length !== 2) {
          this.showAlert(`Formato inválido para ${field}. Use: X,Y`, 'warning');
          return;
        }

        const x = parseInt(coords[0]);
        const y = parseInt(coords[1]);

        if (isNaN(x) || isNaN(y)) {
          this.showAlert(`Coordenadas inválidas para ${field}. Use números.`, 'warning');
          return;
        }

        // Validação dinâmica baseada no tamanho real do container (certificate-preview)
        const certificatePreview = document.querySelector('.certificate-preview');
        const maxX = certificatePreview ? certificatePreview.offsetWidth : 1200;
        const maxY = certificatePreview ? certificatePreview.offsetHeight : 800;

        // Validação mais permissiva - apenas aviso se muito fora
        if (x < -50 || x > maxX + 50 || y < -50 || y > maxY + 50) {
          this.showAlert(`Coordenadas muito distantes da área visível para ${field}`, 'info');
        }
      },

      // Drag & Drop Methods
      handleDragStart(event, elementType) {
        if (this.coordinateMode) return; // Não permitir drag no modo coordenadas

        this.isDragging = true;
        this.draggedElement = elementType;

        const rect = event.target.getBoundingClientRect();
        const previewRect = event.currentTarget.parentElement.getBoundingClientRect();

        this.dragStartPos = {
          x: event.clientX - previewRect.left,
          y: event.clientY - previewRect.top
        };

        this.dragOffset = {
          x: rect.left - previewRect.left,
          y: rect.top - previewRect.top
        };

        event.target.style.opacity = '0.7';
        event.target.style.cursor = 'grabbing';
      },

      handleDragEnd(event, elementType) {
        if (!this.isDragging) return;

        this.isDragging = false;
        this.draggedElement = null;

        event.target.style.opacity = '1';
        event.target.style.cursor = 'grab';
      },

      handleMouseDown(event, elementType) {
        event.preventDefault();
        event.stopPropagation();

        this.draggedElement = elementType;

        const rect = event.target.getBoundingClientRect();
        const previewRect = event.target.closest('.certificate-preview').getBoundingClientRect();

        // Calcular offset do mouse em relação ao elemento
        this.dragOffset = {
          x: event.clientX - rect.left,
          y: event.clientY - rect.top
        };

        event.target.style.cursor = 'grabbing';
        event.target.classList.add('dragging');

        // Adicionar event listeners globais
        document.addEventListener('mousemove', this.handleGlobalMouseMove);
        document.addEventListener('mouseup', this.handleGlobalMouseUp);
      },

      handleMouseMove(event, elementType) {
        if (!this.draggedElement || this.draggedElement !== elementType) return;

        this.updateElementPosition(event, elementType);
      },

      handleMouseUp(event, elementType) {
        if (!this.draggedElement || this.draggedElement !== elementType) return;

        this.finishDragging();
      },

      handleGlobalMouseMove(event) {
        if (!this.draggedElement) return;

        this.updateElementPosition(event, this.draggedElement);
      },

      handleGlobalMouseUp(event) {
        if (!this.draggedElement) return;

        this.finishDragging();
      },

      updateElementPosition(event, elementType) {
        const certificatePreview = document.querySelector('.certificate-preview');
        if (!certificatePreview) return;

        const previewRect = certificatePreview.getBoundingClientRect();

        // Calcular posição do mouse relativa ao container
        const mouseX = event.clientX - previewRect.left - this.dragOffset.x;
        const mouseY = event.clientY - previewRect.top - this.dragOffset.y;

        // Usar as dimensões reais da template para coordenadas consistentes
        const templateImg = document.querySelector('.certificate-preview img');
        let templateWidth = 800;   // Padrão
        let templateHeight = 600;  // Padrão

        // Se a imagem está carregada, usar suas dimensões naturais
        if (templateImg && templateImg.naturalWidth && templateImg.naturalHeight) {
          templateWidth = templateImg.naturalWidth;
          templateHeight = templateImg.naturalHeight;
        }

        const currentWidth = certificatePreview.offsetWidth;
        const currentHeight = certificatePreview.offsetHeight;

        // Calcular coordenadas no sistema PIL (absoluto em pixels da imagem original)
        const referenceX = (mouseX / currentWidth) * templateWidth;
        const referenceY = (mouseY / currentHeight) * templateHeight;

        // Atualizar coordenadas no modelo
        const coordinateField = `${elementType}_coordinates`;
        this.generator[coordinateField] = `${Math.round(referenceX)},${Math.round(referenceY)}`;

        // Forçar re-render para aplicar nova posição
        this.$forceUpdate();
      },

      finishDragging() {
        const draggedElementType = this.draggedElement;
        this.draggedElement = null;

        // Remover event listeners
        document.removeEventListener('mousemove', this.handleGlobalMouseMove);
        document.removeEventListener('mouseup', this.handleGlobalMouseUp);

        // Remover classe dragging de todos os elementos
        document.querySelectorAll('.dragging').forEach(el => {
          el.classList.remove('dragging');
          el.style.cursor = 'grab';
        });

        // Atualizar todos os elementos para refletir as mudanças
        this.$forceUpdate();

        // Mostrar feedback
        if (draggedElementType) {
          this.showAlert(`Posição do ${draggedElementType} atualizada!`, 'success');
        }
      },

      handleFullscreenChange() {
        // Aguardar um pouco para o DOM se ajustar após mudança de tela cheia
        setTimeout(() => {
          this.$forceUpdate();
        }, 200);
      },


    }
  };
</script>

<style scoped>
  .certificate-preview {
    border-radius: 8px;
    padding: 5px;
    background: #f8f9fa;
    overflow: hidden;
    position: relative;
    width: 100%;
    height: 100%;
    min-height: 700px;
  }

  .preview-container {
    position: relative;
    width: 100%;
    height: 100%;
    min-height: 700px;
    background: white;
    border-radius: 4px;
    overflow: hidden;
    border: 1px solid #dee2e6;
  }

  .template-image {
    width: 100%;
    height: 100%;
    border-radius: 4px;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
    pointer-events: none;
  }

  /* Diferentes ajustes da imagem */
  .template-fit-contain {
    object-fit: contain;
  }

  .template-fit-cover {
    object-fit: cover;
  }

  .template-fit-fill {
    object-fit: fill;
  }

  .template-fit-scale-down {
    object-fit: scale-down;
  }



  .text-overlay {
    position: absolute;
    pointer-events: auto;
    z-index: 20;
    max-width: 400px;
    word-wrap: break-word;
    overflow-wrap: break-word;
    cursor: grab;
    user-select: none;
  }

  .text-overlay.central-text {
    white-space: normal;
    max-width: 500px;
    text-align: center;
  }

  .text-overlay:not(.central-text) {
    white-space: nowrap;
  }

  /* Ajustes para tela cheia */
  :fullscreen .certificate-preview,
  :-webkit-full-screen .certificate-preview,
  :-moz-full-screen .certificate-preview,
  :-ms-fullscreen .certificate-preview {
    width: 100vw;
    height: 100vh;
    margin: 0;
    padding: 10px;
    border-radius: 0;
  }

  :fullscreen .preview-container,
  :-webkit-full-screen .preview-container,
  :-moz-full-screen .preview-container,
  :-ms-fullscreen .preview-container {
    width: 100%;
    height: 100%;
    max-width: none;
    max-height: none;
  }

  .draggable-element {
    position: relative;
    padding: 2px 4px;
    border-radius: 2px;
    background: rgba(255, 255, 255, 0.8);
    border: 1px solid transparent;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    display: inline-block;
  }

  .draggable-element:hover {
    border-color: #007bff;
    box-shadow: 0 2px 6px rgba(0, 123, 255, 0.3);
    background: rgba(255, 255, 255, 0.95);
  }

  .draggable-element.dragging {
    cursor: grabbing !important;
    transform: scale(1.02);
    box-shadow: 0 4px 8px rgba(0, 123, 255, 0.4);
    z-index: 1000;
    background: rgba(255, 255, 255, 0.98);
  }

  .element-handle {
    position: absolute;
    top: -6px;
    left: -6px;
    background: #007bff;
    color: white;
    border-radius: 50%;
    width: 12px;
    height: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 6px;
    opacity: 0;
    transition: opacity 0.2s ease;
    cursor: grab;
  }

  .draggable-element:hover .element-handle {
    opacity: 1;
  }



  .form-control-color {
    width: 100%;
    height: 38px;
  }

  .btn-header {
    margin-left: 10px;
  }

  @media (max-width: 768px) {
    .preview-container {
      transform: scale(0.8);
      transform-origin: top left;
    }
  }





  /* Otimizações para layout compacto */
  .form-control-sm {
    padding: 0.25rem 0.5rem;
    font-size: 0.875rem;
    border-radius: 0.2rem;
  }

  .small {
    font-size: 0.875rem;
  }

  .card-body {
    padding: 1rem;
  }

  .mb-2 {
    margin-bottom: 0.5rem !important;
  }

  .mb-3 {
    margin-bottom: 1rem !important;
  }

  .g-2 {
    gap: 0.5rem !important;
  }

  /* Preview otimizado */
  .certificate-preview {
    min-height: 400px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .preview-container {
    max-width: 100%;
    position: relative;
  }

  /* Otimizações quando sidebar está fechada */
  .sidebar-toggled .certificate-preview {
    min-height: 500px;
  }

  .sidebar-toggled .preview-container {
    max-width: 90%;
  }

  /* Aproveitar todo o espaço quando sidebar está fechada */
  .sidebar-toggled .col-lg-3 {
    flex: 0 0 25% !important;
    max-width: 25% !important;
  }

  .sidebar-toggled .col-lg-9 {
    flex: 0 0 75% !important;
    max-width: 75% !important;
  }

  /* Responsividade melhorada */
  @media (max-width: 1200px) {
    .col-lg-3 {
      flex: 0 0 30%;
      max-width: 30%;
    }

    .col-lg-9 {
      flex: 0 0 70%;
      max-width: 70%;
    }
  }

  @media (max-width: 992px) {
    .col-lg-3 {
      flex: 0 0 35%;
      max-width: 35%;
    }

    .col-lg-9 {
      flex: 0 0 65%;
      max-width: 65%;
    }
  }
</style>